import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CircleCheck, CircleHelp, CircleX, Info, Menu, Shield, TriangleAlert } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuthStore } from "@/store/auth.store";
import { useSelectTenant, useTenant, useTenants } from "@/hooks/useTenants";
import { authService } from "@/services/auth.service";
import { hotelPropertiesService } from "@/services/hotel-properties.service";
import type { HotelProperty, Tenant } from "@/types";
import { CommandSearchButton } from "./topbar/CommandSearchButton";
import { HotelPropertySelector } from "./topbar/HotelPropertySelector";
import { ImpersonationBanner } from "./topbar/ImpersonationBanner";
import { NotificationsPopover } from "./topbar/NotificationsPopover";
import { TenantSelector } from "./topbar/TenantSelector";
import { TopbarActions } from "./topbar/TopbarActions";
import { UserMenu } from "./topbar/UserMenu";
import type { TopbarNotification } from "./topbar/topbarTypes";
import { isSystemOwner } from "@/lib/authz";
import { DetailedHelpPanel } from "@/components/onboarding-tour/DetailedHelpPanel";

const DETAILED_HELP_DISCOVERED_KEY = "innsight_detailed_help_discovered";

interface TopbarProps {
  onNewReservation: () => void;
  onOpenCommand: () => void;
  onToggleNavigation: () => void;
}

export function Topbar({ onNewReservation, onOpenCommand, onToggleNavigation }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [tenantSelectorOpen, setTenantSelectorOpen] = useState(false);
  const [propertySelectorOpen, setPropertySelectorOpen] = useState(false);
  const [detailedHelpOpen, setDetailedHelpOpen] = useState(false);
  const [detailedHelpDiscovered, setDetailedHelpDiscovered] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(DETAILED_HELP_DISCOVERED_KEY) === "true",
  );
  const notifRef = useRef<HTMLDivElement>(null);
  const tenantSelectorRef = useRef<HTMLDivElement>(null);
  const propertySelectorRef = useRef<HTMLDivElement>(null);
  const { theme, resolvedTheme, setTheme } = useTheme();
  const tenantId = useAuthStore((state) => state.tenantId);
  const propertyId = useAuthStore((state) => state.propertyId);
  const setPropertyId = useAuthStore((state) => state.setPropertyId);
  const user = useAuthStore((state) => state.user);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const leaveImpersonation = useAuthStore((state) => state.leaveImpersonation);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: tenant } = useTenant(tenantId ?? 0);
  const { data: rawTenants, isError: tenantsError } = useTenants();
  const { data: rawProperties = [] } = useQuery({
    queryKey: ["hotel-properties", tenantId],
    queryFn: hotelPropertiesService.list,
    enabled: !!user && !!tenantId,
  });

  const { data: unreadData } = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: async () => {
      const { data } = await (await import("@/lib/axios")).api.get(
        "/v1/notificacoes/quantidade-nao-lida",
      );
      return (data.data?.count ?? 0) as number;
    },
    refetchInterval: 60_000,
    enabled: !!user,
  });

  const { data: persistedNotifications = [] } = useQuery({
    queryKey: ["notifications", "topbar"],
    queryFn: async () => {
      const { data } = await (await import("@/lib/axios")).api.get(
        "/v1/notificacoes?per_page=6&page=1",
      );
      return (data.data?.data ?? []) as Array<{
        id: string;
        data: { title?: string; message?: string; icon?: string };
        read_at: string | null;
        created_at: string;
      }>;
    },
    refetchInterval: 60_000,
    enabled: !!user,
  });

  const markAllMut = useMutation({
    mutationFn: async () =>
      (await import("@/lib/axios")).api.patch("/v1/notificacoes/ler-todas"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markOneMut = useMutation({
    mutationFn: async (id: string) =>
      (await import("@/lib/axios")).api.patch(`/v1/notificacoes/${id}/ler`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const selectTenant = useSelectTenant();
  const activeTenants = useMemo<Tenant[]>(() => {
    if (tenantsError || !Array.isArray(rawTenants)) return [];
    return rawTenants.filter((item) => item.is_active !== false);
  }, [rawTenants, tenantsError]);
  const hasMultipleProperties = activeTenants.length > 1;

  useEffect(() => {
    if (!tenantId && activeTenants.length > 0 && !isImpersonating && !isSystemOwner(user)) {
      selectTenant(activeTenants[0].id);
    }
  }, [activeTenants, isImpersonating, selectTenant, tenantId, user]);
  const activeProperties: HotelProperty[] = rawProperties.filter(
    (property) => property.is_active !== false,
  );
  const selectedProperty =
    activeProperties.find((property) => property.id === propertyId) ??
    activeProperties[0] ??
    null;
  const hasMultipleHotelProperties = activeProperties.length > 1;

  const handleLeaveImpersonation = async () => {
    try {
      await authService.leaveImpersonation();
    } catch {
      // A limpeza local ainda precisa ocorrer mesmo quando o servidor já encerrou a sessão.
    }
    leaveImpersonation();
    window.location.href = "/visao-geral";
  };

  useEffect(() => {
    if (!activeProperties.length) {
      setPropertyId(null);
      return;
    }

    if (
      !propertyId ||
      !activeProperties.some((property) => property.id === propertyId)
    ) {
      setPropertyId(activeProperties[0].id);
    }
  }, [activeProperties, propertyId, setPropertyId]);

  const notifications = useMemo<TopbarNotification[]>(() => {
    const icons = {
      warning: { icon: TriangleAlert, color: "text-amber-300", bg: "bg-amber-500/10" },
      error: { icon: CircleX, color: "text-red-300", bg: "bg-red-500/10" },
      success: { icon: CircleCheck, color: "text-emerald-300", bg: "bg-emerald-500/10" },
      info: { icon: Info, color: "text-cyan-300", bg: "bg-cyan-500/10" },
    };

    return persistedNotifications.map((notification) => {
      const visual = icons[notification.data.icon as keyof typeof icons] ?? {
        icon: Bell,
        color: "text-primary",
        bg: "bg-primary/10",
      };

      return {
        id: notification.id,
        ...visual,
        title: notification.data.title ?? "Aviso do Innsight",
        message: notification.data.message ?? "",
        time: new Date(notification.created_at).toLocaleDateString("pt-BR"),
        category: ((notification.data as { category?: "operational" | "system" | "message" }).category) ?? "system",
      };
    });
  }, [persistedNotifications]);

  const readIds = persistedNotifications
    .filter((notification) => notification.read_at !== null)
    .map((notification) => notification.id);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
      if (
        tenantSelectorRef.current &&
        !tenantSelectorRef.current.contains(event.target as Node)
      ) {
        setTenantSelectorOpen(false);
      }
      if (
        propertySelectorRef.current &&
        !propertySelectorRef.current.contains(event.target as Node)
      ) {
        setPropertySelectorOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const dbUnread = unreadData ?? 0;
  const unreadCount = dbUnread;

  return (
    <>
      {isImpersonating ? (
        <ImpersonationBanner
          userName={user?.name}
          onLeave={handleLeaveImpersonation}
        />
      ) : null}
      <header className="sticky top-0 z-30 h-[70px] border-b border-border bg-card dark:bg-background/90 dark:backdrop-blur-xl">
        <div className="h-full flex items-center justify-between px-3 md:px-7 gap-2 md:gap-4">
          <button
            type="button"
            onClick={onToggleNavigation}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-border/70 dark:bg-card/60 md:hidden"
            aria-label="Abrir menu de navegação"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden min-w-0 flex-1 md:block">
            <CommandSearchButton onOpenCommand={onOpenCommand} />
          </div>

          <div className="flex min-w-0 items-center gap-1.5 md:gap-3">
            {isSystemOwner(user) ? (
              <button
                type="button"
                onClick={() => navigate("/administracao/telemetria")}
                className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-cyan-500/35 bg-cyan-500/10 px-3 text-sm font-semibold text-cyan-800 transition hover:bg-cyan-500/15 dark:text-cyan-100"
                aria-label="Abrir Administração Allmight"
              >
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Allmight</span>
              </button>
            ) : null}

            {!isSystemOwner(user) || isImpersonating ? <TenantSelector
              selectorRef={tenantSelectorRef}
              open={tenantSelectorOpen}
              onOpenChange={setTenantSelectorOpen}
              tenant={tenant}
              tenantId={tenantId}
              activeTenants={activeTenants}
              hasMultipleProperties={hasMultipleProperties}
              onSelectTenant={(id) => {
                selectTenant(id);
                setTenantSelectorOpen(false);
              }}
            /> : null}

            {!isSystemOwner(user) || isImpersonating ? <HotelPropertySelector
              selectorRef={propertySelectorRef}
              open={propertySelectorOpen}
              onOpenChange={setPropertySelectorOpen}
              selectedProperty={selectedProperty}
              activeProperties={activeProperties}
              hasMultipleHotelProperties={hasMultipleHotelProperties}
              onSelectProperty={(id) => {
                setPropertyId(id);
                setPropertySelectorOpen(false);
                queryClient.invalidateQueries();
              }}
            /> : null}

            <button
              type="button"
              onClick={() => {
                setDetailedHelpOpen(true);
                if (!detailedHelpDiscovered) {
                  window.localStorage.setItem(DETAILED_HELP_DISCOVERED_KEY, "true");
                  setDetailedHelpDiscovered(true);
                }
              }}
              aria-label="Abrir ajuda detalhada desta tela"
              title="Ajuda detalhada desta tela"
              className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <CircleHelp className="h-5 w-5" />
              {!detailedHelpDiscovered ? (
                <span className="absolute right-1.5 top-1.5 flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
                </span>
              ) : null}
            </button>

            <DetailedHelpPanel open={detailedHelpOpen} onClose={() => setDetailedHelpOpen(false)} />

            <NotificationsPopover
              popoverRef={notifRef}
              open={notifOpen}
              onOpenChange={setNotifOpen}
              notifications={notifications}
              readIds={readIds}
              unreadCount={unreadCount}
              dbUnread={dbUnread}
              onMarkNotificationRead={(id) =>
                markOneMut.mutate(id)
              }
              onMarkAllRead={() => {
                markAllMut.mutate();
              }}
              onViewAll={() => {
                setNotifOpen(false);
                navigate("/notificacoes");
              }}
            />

            <TopbarActions
              theme={resolvedTheme ?? theme}
              onThemeToggle={() =>
                setTheme((resolvedTheme ?? theme) === "dark" ? "light" : "dark")
              }
              onNewReservation={onNewReservation}
            />
            <UserMenu />
          </div>
        </div>
      </header>
    </>
  );
}

export default Topbar;
